import { useEffect, useRef, useState } from "react"

/** How long a step shows its saved status before it closes. */
export const SAVED_FEEDBACK_MS = 600

export type PricingSaveGuard = {
  /** The save landed; the step shows its saved status until it closes. */
  readonly justSaved: boolean
  /** Claims the save; false while one is already running. */
  readonly start: () => boolean
  /** Lets the next save start (the request settled). */
  readonly release: () => void
  /** Releases, then closes: after the saved status when the step shows one. */
  readonly succeed: () => void
}

/**
 * The synchronous in-flight guard and the close-after-save every pricing step
 * shares. `isLoading` flips after render, so a double-click in the same tick
 * would otherwise send two requests.
 */
export const usePricingSaveGuard = ({
  onClose,
  savedFeedback,
}: {
  readonly onClose: () => void
  /** Show the saved status for SAVED_FEEDBACK_MS before closing. */
  readonly savedFeedback: boolean
}): PricingSaveGuard => {
  const [justSaved, setJustSaved] = useState(false)
  const inFlight = useRef(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (closeTimer.current != null) clearTimeout(closeTimer.current)
    },
    [],
  )

  const release = (): void => {
    inFlight.current = false
  }

  return {
    justSaved,
    start: () => {
      if (inFlight.current) return false
      inFlight.current = true
      return true
    },
    release,
    succeed: () => {
      release()
      if (!savedFeedback) {
        onClose()
        return
      }
      setJustSaved(true)
      closeTimer.current = setTimeout(onClose, SAVED_FEEDBACK_MS)
    },
  }
}
